import { Component, EventEmitter, forwardRef, input, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ChangeContext, ChangeType, Direction, RoomOption } from './room-select.interface';
import { collapseExpand } from '../../animations/animations';

const ROOM_SELECT_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  /* tslint:disable-next-line: no-use-before-declare */
  useExisting: forwardRef(() => RoomSelectComponent),
  multi: true,
};

@Component({
  selector: 'rx-room-select',
  templateUrl: './room-select.component.html',
  styleUrls: ['./room-select.component.scss'],
  providers: [ROOM_SELECT_CONTROL_VALUE_ACCESSOR],
  animations: [collapseExpand],
})
export class RoomSelectComponent implements ControlValueAccessor {
  /** Limit number of adults */
  @Input() maxAdults: number;

  /** Limit number of rooms  */
  @Input() maxRooms = 8;

  /** Label text */
  @Input() label?: string;

  /** 'Add room' button */
  @Input() textAddRoom = 'Add room';

  /** 'Apply' button text */
  @Input() textApply = 'Apply';

  /** Text to replace the word 'Room'  */
  @Input() textRoomSingle = 'Room';

  /** text to replace the word 'Rooms'*/
  @Input() textRoomPlural = 'Rooms';

  /** text to replace the word 'Adult' */
  @Input() textAdultSingle = 'Adult';

  /** text to replace the word 'Adults'*/
  @Input() textAdultPlural = 'Adults';

  /** Occupancy search will lift the limit of 4 persons per room */
  occupancySearch = input(false);

  /** Toggle dropdown */
  @Output() toggle: EventEmitter<RoomOption> = new EventEmitter();

  /**
   * Callback fired when a room is added or removed.
   * Accepts a ChangeContext object which contains details of the change
   *  **/
  @Output() roomChange: EventEmitter<ChangeContext> = new EventEmitter();

  /**
   * Callback fired when a room is added and the limit has been reached.
   *  **/
  @Output() roomLimitReached: EventEmitter<void> = new EventEmitter<void>();

  public isShow = false;
  public isShowRoom = false;
  public isShowAdultRoom = false;
  public adult = 1;
  public room = 1;

  qaPrefix = 'room-select-';

  totalAdult = 1;
  roomAdultsArray: RoomOption[] = [
    {
      room: this.room,
      adult: this.adult,
    },
  ];

  selectionOption: RoomOption = {
    room: this.room,
    adult: this.totalAdult,
  };

  get direction() {
    return Direction;
  }

  onChange = (value: any) => {
    /**/
  };
  registerOnChange = (fn: (val: any) => void) => (this.onChange = fn);

  registerOnTouched(fn: any): void {
    /**/
  }

  setDisabledState(isDisabled: boolean): void {
    /**/
  }

  writeValue = (value: number[]) => {
    this.roomAdultsArray = [];
    value.forEach((val, idx) => {
      if (val > 0) {
        this.room = idx + 1;
        this.roomAdultsArray.push({ room: idx + 1, adult: val });
      }
    });
    this.totalAdult = this.roomAdultsArray.reduce((sum, item) => sum + item.adult, 0);
    this.onChange(value);
  };

  public isOpenRoom() {
    this.isShow = !this.isShow;
  }

  // Start Add Remove Adult event
  addRemoveAdult(operator: Direction, option: RoomOption) {
    if (operator === Direction.REMOVE) {
      if (option.adult === 1) {
        return;
      }
      this.roomAdultsArray.filter((data) => {
        if (option.room === data.room) {
          option.adult -= 1;
          this.adult = option.adult;
        }
      });
    }
    if (operator === Direction.ADD) {
      if (option.adult === this.maxAdults && !this.occupancySearch()) {
        return;
      }
      this.roomAdultsArray.filter((data) => {
        if (option.room === data.room) {
          option.adult += 1;
          this.adult = option.adult;
        }
      });
    }
    this.totalAdult = this.roomAdultsArray.reduce((sum, item) => sum + item.adult, 0);
    this.roomChange.emit(this.getChangeContext(ChangeType.ADULT, operator));
  }

  // Start Apply Room event
  applyRoom() {
    const csvRooms = new Array(this.maxRooms).fill(0);
    this.roomAdultsArray.forEach((roomAdult) => (csvRooms[roomAdult.room - 1] = roomAdult.adult));
    this.selectionOption.adult = this.totalAdult;
    this.selectionOption.room = this.room;
    this.toggle.emit(this.selectionOption);
    this.onChange(csvRooms);
    this.isShow = false;
  }

  // Start Add new Room event
  addRoom() {
    if (this.room >= this.maxRooms) {
      this.roomLimitReached.emit();
      return;
    }

    this.room += 1;
    this.roomAdultsArray.push({ room: this.room, adult: 1 });
    this.totalAdult = this.roomAdultsArray.reduce((sum, item) => sum + item.adult, 0); // total of adult

    if (this.room >= this.maxRooms) {
      this.isShowRoom = true;
    }

    this.roomChange.emit(this.getChangeContext(ChangeType.ROOM, Direction.ADD));
  }

  // Start Remove Adult room event
  removeAdultRoom(data, index) {
    this.isShowAdultRoom = true;
    this.roomAdultsArray.splice(index, 1);
    this.roomAdultsArray.sort();
    this.roomAdultsArray.map((roomOption, idx) => {
      // ordering room
      return (roomOption.room = idx + 1);
    });
    this.room = this.roomAdultsArray.length; // length of room
    this.totalAdult = this.roomAdultsArray.reduce((sum, item) => sum + item.adult, 0); // total of adult
    this.roomChange.emit(this.getChangeContext(ChangeType.ROOM, Direction.REMOVE));
  }

  public handleClickOutside(element: HTMLElement) {
    if (element.classList.contains('rx-close') || element.classList.contains('modal')) {
      this.isShow = true;
      return;
    }
    this.isShow = false;
    if (this.selectionOption.adult !== this.totalAdult || this.selectionOption.room !== this.room) {
      this.applyRoom();
    }
    if (this.isShowRoom || this.isShowAdultRoom) {
      this.isShowRoom = false;
      this.isShowAdultRoom = false;
      this.isShow = true;
    }
  }

  formatPlurals(count: number, labelSingle: string, labelPlural: string, hideNumber = false): string {
    if (hideNumber) {
      return count > 1 ? labelPlural : labelSingle;
    }
    return count > 1 ? `${count} ${labelPlural}` : `${count} ${labelSingle}`;
  }

  private getChangeContext(changeType: ChangeType, direction: Direction): ChangeContext {
    const changeContext: ChangeContext = new ChangeContext();
    changeContext.changeType = changeType;
    changeContext.direction = direction;
    changeContext.state = this.roomAdultsArray;
    return changeContext;
  }
}
